import { FocusSummaryCard } from '@/shared/components';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { sparklineToChartData } from '@/shared/lib/sparkline-chart';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';
import { getAlertsPerResidentQueryOptions } from '../api/get-alerts-per-resident.query';

type AlertsPerResidentCardProps = {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const AlertsPerResidentCard: FC<AlertsPerResidentCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();

  const { data, isLoading } = useQuery(
    getAlertsPerResidentQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  // More alerts per resident is a worse outcome, so a rise is shown in red.
  const indicator =
    data?.changePct != null
      ? {
          value: data.changePct,
          negativeValue: data.changePct < 0,
          negativeAsPositive: true,
        }
      : undefined;

  return (
    <FocusSummaryCard
      showChart
      icon="warning-triangle"
      title="Alerts per Resident"
      tooltipMessage="Average number of alerts created per resident during the selected period."
      indicator={indicator}
      value={
        data?.alertsPerResident != null ? `${data.alertsPerResident}` : 'N/A'
      }
      chart={
        data?.sparkline
          ? { data: sparklineToChartData(data.sparkline) }
          : undefined
      }
      loading={isLoading}
    />
  );
};
